import {api} from './api';
import {
  Display,
  DisplaysResponse,
  DisplayPhoto,
  DisplayPhotosResponse,
  DisplayCalendar,
  DisplayCalendarsResponse,
  CreateDisplayRequest,
  UpdateDisplayRequest,
  AddDisplayPhotoRequest,
  UpdateDisplayPhotoRequest,
  AddDisplayCalendarRequest,
  UpdateDisplayCalendarRequest,
  FrameData,
} from '../types';

export const displayService = {
  // Display CRUD
  async getDisplays(): Promise<DisplaysResponse> {
    return api.get<DisplaysResponse>('/api/v1/displays');
  },

  async createDisplay(request?: CreateDisplayRequest): Promise<Display> {
    return api.post<Display>('/api/v1/displays', request || {});
  },

  async getDisplay(displayId: string): Promise<Display> {
    return api.get<Display>(`/api/v1/displays/${displayId}`);
  },

  async updateDisplay(
    displayId: string,
    request: UpdateDisplayRequest,
  ): Promise<Display> {
    return api.patch<Display>(`/api/v1/displays/${displayId}`, request);
  },

  async deleteDisplay(displayId: string): Promise<void> {
    await api.delete(`/api/v1/displays/${displayId}`);
  },

  async setDefaultDisplay(displayId: string): Promise<Display> {
    return api.post<Display>(`/api/v1/displays/${displayId}/set-default`);
  },

  // Display Photos
  async getDisplayPhotos(displayId: string): Promise<DisplayPhotosResponse> {
    return api.get<DisplayPhotosResponse>(
      `/api/v1/displays/${displayId}/photos`,
    );
  },

  async addPhotoToDisplay(
    displayId: string,
    request: AddDisplayPhotoRequest,
  ): Promise<DisplayPhoto> {
    return api.post<DisplayPhoto>(
      `/api/v1/displays/${displayId}/photos`,
      request,
    );
  },

  async updateDisplayPhoto(
    displayId: string,
    photoId: string,
    request: UpdateDisplayPhotoRequest,
  ): Promise<DisplayPhoto> {
    return api.put<DisplayPhoto>(
      `/api/v1/displays/${displayId}/photos/${photoId}`,
      request,
    );
  },

  async removePhotoFromDisplay(
    displayId: string,
    photoId: string,
  ): Promise<void> {
    await api.delete(`/api/v1/displays/${displayId}/photos/${photoId}`);
  },

  // Display Calendars
  async getDisplayCalendars(
    displayId: string,
  ): Promise<DisplayCalendarsResponse> {
    return api.get<DisplayCalendarsResponse>(
      `/api/v1/displays/${displayId}/calendars`,
    );
  },

  async addCalendarToDisplay(
    displayId: string,
    request: AddDisplayCalendarRequest,
  ): Promise<DisplayCalendar> {
    return api.post<DisplayCalendar>(
      `/api/v1/displays/${displayId}/calendars`,
      request,
    );
  },

  async updateDisplayCalendar(
    displayId: string,
    calendarId: string,
    request: UpdateDisplayCalendarRequest,
  ): Promise<DisplayCalendar> {
    return api.patch<DisplayCalendar>(
      `/api/v1/displays/${displayId}/calendars/${calendarId}`,
      request,
    );
  },

  async removeCalendarFromDisplay(
    displayId: string,
    calendarId: string,
  ): Promise<void> {
    await api.delete(`/api/v1/displays/${displayId}/calendars/${calendarId}`);
  },

  // Frame Data for Display
  async getDisplayFrameData(displayId?: string): Promise<FrameData> {
    const url = displayId
      ? `/api/v1/frame/data?displayId=${displayId}`
      : '/api/v1/frame/data';
    return api.get<FrameData>(url);
  },
};
